import { buildGapScene } from "../../utils/dispatchGeotabGap.js";
import { attachRouteMapBasemap } from "../../utils/routeMapBasemap.js";
import { fmtTime } from "../geotab/geotabFormat.js";

// Carte du lecteur d'écart Geotab : route prévue en pointillé, trace GPS Geotab tracée au fil de la relecture, arrêts
// prévus numérotés comme au Gantt (✓ faits, ✕ pas faits), position du camion et point de l'arrêt hors du Dispatch.
// Sur chaque différence, la carte s'approche un peu du lieu, puis revient.

const near = (a, b) => a != null && b != null && Math.abs(a - b) < 0.5;
const ok = (lat, lon) => Number.isFinite(lat) && Number.isFinite(lon);

// Étapes d'un lieu : « 4-5 · 8-9 ».
function stepsLabel(steps) {
  const runs = [];
  [...steps].sort((a, b) => a - b).forEach((n) => { const last = runs[runs.length - 1]; if (last && n === last[1] + 1) last[1] = n; else runs.push([n, n]); });
  return runs.map(([a, b]) => (a === b ? `${a}` : `${a}-${b}`)).join(" · ");
}

export function mountGapMap(container, { day, item }) {
  const L = window.L;
  if (!L || !container) return null;
  const scene = buildGapScene(day, item);
  const map = L.map(container, { zoomControl: false, zoomSnap: 0.25, attributionControl: false });
  const disposeBasemap = attachRouteMapBasemap(map, { L });
  const icon = (html, className = "") => L.divIcon({ className, html, iconSize: [0, 0] });
  const bounds = [];

  if (day.geometry?.length > 1) {
    L.polyline(day.geometry, { color: "#475569", weight: 3, dashArray: "6 6", opacity: 0.8, interactive: false }).addTo(map);
    bounds.push(...day.geometry);
  }
  const casing = L.polyline([], { color: "#fff", weight: 7, opacity: 0.9, interactive: false }).addTo(map);
  const real = L.polyline([], { color: "#3f5585", weight: 4, interactive: false }).addTo(map);

  // Arrêts prévus regroupés par lieu ; quand on sait qu'ils sont faits ou pas.
  const missedAt = new Map(scene.missingGroups.flatMap((group) => group.sites.map((site) => [site.siteIdx, group.known])));
  const doneAt = new Map(day.visits.filter((v) => v.site_idx != null).map((v) => [v.site_idx, v.arr]));
  const places = new Map();
  (day.sites || []).filter((site) => site.kind !== "depot" && ok(site.lat, site.lon)).forEach((site) => {
    const key = `${site.lat.toFixed(4)},${site.lon.toFixed(4)}`;
    const entry = places.get(key) || { lat: site.lat, lon: site.lon, kind: site.kind, name: site.name, sites: [] };
    entry.sites.push(site);
    places.set(key, entry);
  });
  const pins = [...places.values()].map((entry) => {
    const node = document.createElement("div");
    node.className = "gg-pin";
    node.dataset.kind = entry.kind === "loading" ? "loading" : "delivery";
    node.title = entry.name;
    node.innerHTML = `<span></span><i class="gg-pin-ok">✓</i><i class="gg-pin-ko">✕</i>`;
    node.firstChild.textContent = stepsLabel(entry.sites.flatMap((site) => site.steps || []));
    L.marker([entry.lat, entry.lon], { icon: L.divIcon({ className: "", html: node, iconSize: [0, 0] }), interactive: false }).addTo(map);
    bounds.push([entry.lat, entry.lon]);
    const missed = entry.sites.filter((site) => missedAt.has(site.idx)).map((site) => missedAt.get(site.idx));
    return {
      node, all: missed.length === entry.sites.length, missedAt: missed.length ? Math.max(...missed) : Infinity,
      doneAt: Math.min(...entry.sites.map((site) => doneAt.get(site.idx) ?? Infinity)),
    };
  });
  if (Array.isArray(day.depot) && ok(...day.depot)) {
    L.marker(day.depot, { icon: icon('<div class="gg-pin" data-kind="depot"><span>D</span></div>'), interactive: false }).addTo(map);
    bounds.push(day.depot);
  }
  const visits = (day.visits || []).filter((v) => ok(v.lat, v.lon));
  visits.forEach((v) => bounds.push([v.lat, v.lon]));

  // Position du camion ; arrêt hors du Dispatch : seulement le point, la route garde sa couleur.
  const dot = L.marker(bounds[0] || [45.7, -73], { icon: icon('<div class="gg-dot"></div>'), interactive: false, zIndexOffset: 900 });
  const stops = item.stops || [];
  const extraVisit = visits.find((v) => stops.some((stop) => near(stop.arr, v.arr)));
  let gap = null;
  if (extraVisit) {
    const label = `${stops.map((stop) => stop.place || "Lieu sans nom").join(" → ")} · ${fmtTime(stops[0].arr)} → ${fmtTime(stops[stops.length - 1].dep)}`;
    const node = document.createElement("div");
    node.className = "gg-gap";
    node.innerHTML = "<b></b>";
    node.firstChild.textContent = label;
    gap = L.marker([extraVisit.lat, extraVisit.lon], { icon: L.divIcon({ className: "", html: node, iconSize: [0, 0] }), interactive: false, zIndexOffset: 1000 });
  }

  const base = bounds.length ? L.latLngBounds(bounds) : null;
  if (base) map.fitBounds(base, { padding: [30, 30], animate: false }); else map.setView([45.7, -73.0], 8);
  const baseZoom = map.getZoom();
  const observer = new ResizeObserver(() => map.invalidateSize());
  observer.observe(container);

  let trace = [], lastT = null;
  const view = {
    setTrace(points) { trace = points || []; if (lastT != null) view.setTime(lastT); },
    setTime(t) {
      lastT = t;
      let i = -1;
      for (let lo = 0, hi = trace.length - 1; lo <= hi;) { const mid = (lo + hi) >> 1; if (trace[mid][2] <= t) { i = mid; lo = mid + 1; } else hi = mid - 1; }
      const line = trace.slice(0, i + 1).map((pt) => [pt[0], pt[1]]);
      real.setLatLngs(line); casing.setLatLngs(line);
      // Sans tracé : le dernier arrêt atteint.
      const at = i >= 0 ? line[line.length - 1] : (() => { const v = [...visits].reverse().find((visit) => visit.arr <= t); return v ? [v.lat, v.lon] : day.depot; })();
      if (at && ok(...at)) { dot.setLatLng(at); if (!map.hasLayer(dot)) dot.addTo(map); }
      if (gap) { const shown = stops[0].arr <= t; if (shown && !map.hasLayer(gap)) gap.addTo(map); if (!shown && map.hasLayer(gap)) gap.remove(); }
      pins.forEach((pin) => {
        const missed = t >= pin.missedAt;
        pin.node.dataset.done = t >= pin.doneAt ? "true" : "false";
        pin.node.dataset.missed = missed ? (pin.all ? "all" : "part") : "false";
      });
    },
    end() { if (!day.in_progress && map.hasLayer(dot)) dot.remove(); },
    // Petit rapprochement sur le lieu d'une différence.
    focus(ev) {
      const pts = ev.kind === "extra" ? (extraVisit ? [[extraVisit.lat, extraVisit.lon]] : [])
        : ev.group.sites.map((s) => (day.sites || []).find((site) => site.idx === s.siteIdx)).filter((site) => site && ok(site.lat, site.lon)).map((site) => [site.lat, site.lon]);
      if (!pts.length) return;
      const zoom = baseZoom + 0.75;
      if (pts.length === 1) map.flyTo(pts[0], zoom, { duration: 0.9 });
      else map.flyToBounds(pts, { maxZoom: zoom, padding: [60, 60], duration: 0.9 });
    },
    unfocus() { if (base && Math.abs(map.getZoom() - baseZoom) > 0.01) map.flyToBounds(base, { padding: [30, 30], duration: 0.7 }); },
    destroy() { observer.disconnect(); disposeBasemap?.(); map.remove(); },
  };
  return view;
}
